import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';

interface AudioContextType {
    currentPlaylist: string | null;
    setCurrentPlaylist: (id: string | null) => void;
    selectedTrack: number;
    setSelectedTrack: (index: number) => void;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    isMuted: boolean;
    setIsMuted: (muted: boolean) => void;
    audioRef: React.RefObject<HTMLAudioElement | null>;
    savedPosition: number;
    setSavedPosition: (position: number) => void;
    totalTracks: number;
    setTotalTracks: (total: number) => void;
    onTrackEnd: (() => void) | null;
    setOnTrackEnd: (callback: (() => void) | null) => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within AudioProvider');
    }
    return context;
};

interface AudioProviderProps {
    children: ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
    const [currentPlaylist, setCurrentPlaylist] = useState<string | null>(null);
    const [selectedTrack, setSelectedTrack] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [savedPosition, setSavedPosition] = useState(0);
    const [totalTracks, setTotalTracks] = useState(0);
    const [onTrackEnd, setOnTrackEnd] = useState<(() => void) | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    const handleEnded = () => {
        setIsPlaying(false);
        setSavedPosition(0);

        // Call the registered callback to advance to next track
        if (onTrackEnd) {
            onTrackEnd();
        }
    };

    return (
        <AudioContext.Provider
            value={{
                currentPlaylist,
                setCurrentPlaylist,
                selectedTrack,
                setSelectedTrack,
                isPlaying,
                setIsPlaying,
                isMuted,
                setIsMuted,
                audioRef,
                savedPosition,
                setSavedPosition,
                totalTracks,
                setTotalTracks,
                onTrackEnd,
                setOnTrackEnd,
            }}
        >
            {children}
            <audio ref={audioRef} onEnded={handleEnded} style={{ display: 'none' }} />
        </AudioContext.Provider>
    );
};
